import { useState } from "react";
import PropTypes from "prop-types";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import ActionDialog from "components/admin/ActionDialog";
import { toLocalInput } from "utils/format";
import { useRunner } from "components/admin/hooks";
import api from "api/client";
import AppearanceEditor from "./AppearanceEditor";
import ItemsEditor from "./ItemsEditor";
import CraftEditor from "./CraftEditor";
import { parseJson, toInputTime } from "./shared";

const TABS = ["Cơ bản", "NPC và chữ", "Vật phẩm", "Chế đồ", "Nâng cao (kỹ thuật)"];
const DURATIONS = [3, 7, 14, 30];

const plusDays = (value, days) => {
  const date = value ? new Date(value) : new Date();
  return toLocalInput(new Date(date.getTime() + days * 86400000));
};

/** "13:25 2/11/2026" */
const fmt = (value) => {
  if (!value) return "";
  const d = new Date(value);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())} ${d.toLocaleDateString("vi-VN")}`;
};

function clone(value) {
  return JSON.parse(JSON.stringify(value || {}));
}

/**
 * Tạo mùa mới (season = null) hoặc sửa mùa có sẵn. Mọi tab cùng sửa một bản cấu hình;
 * tab JSON để sửa những gì form chưa có.
 */
function SeasonDialog({ season, kinds, onClose, onDone }) {
  const [run, busy] = useRunner();
  const editing = Boolean(season);
  const started = editing && season.status !== 0;
  const [tab, setTab] = useState(0);
  const [form, setForm] = useState(() => ({
    kind: editing ? season.kind : kinds[0]?.key || "",
    name: editing ? season.name : kinds[0]?.name || "",
    startAt: editing ? toInputTime(season.startAt) : "",
    endAt: editing ? toInputTime(season.endAt) : "",
    viewDays: editing ? season.viewDays : 3,
  }));
  const [config, setConfig] = useState(() =>
    parseJson(editing ? season.config : kinds[0]?.config, {})
  );
  const [jsonText, setJsonText] = useState("");
  const [jsonError, setJsonError] = useState(null);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const update = (fn) =>
    setConfig((current) => {
      const next = clone(current);
      fn(next);
      return next;
    });

  const pickTab = (value) => {
    if (value === 4) {
      setJsonText(JSON.stringify(config, null, 2));
      setJsonError(null);
    }
    setTab(value);
  };

  const pickKind = (key) => {
    const kind = kinds.find((row) => row.key === key);
    set({ kind: key, name: kind?.name || form.name });
    setConfig(parseJson(kind?.config, {}));
  };

  const bonus = config.modules?.exchange_bonus?.percent ?? "";
  const setBonus = (value) =>
    update((next) => {
      next.modules = next.modules || {};
      const percent = Number(value) || 0;
      if (percent > 0)
        next.modules.exchange_bonus = { ...(next.modules.exchange_bonus || {}), percent };
      else delete next.modules.exchange_bonus;
    });

  const startDate = form.startAt ? new Date(form.startAt) : null;
  const endDate = form.endAt ? new Date(form.endAt) : null;
  const spanDays =
    startDate && endDate ? Math.round(((endDate - startDate) / 86400000) * 10) / 10 : null;
  const blockers = [
    !form.name.trim() && "Chưa đặt tên mùa.",
    !editing && !form.startAt && "Chưa chọn giờ bắt đầu.",
    !form.endAt && "Chưa chọn giờ kết thúc.",
    startDate && endDate && endDate <= startDate && "Giờ kết thúc phải sau giờ bắt đầu.",
    jsonError && "Tab Nâng cao đang có cấu hình sai, sửa lại hoặc đóng hộp này.",
  ].filter(Boolean);

  const submit = async () => {
    if (blockers.length) return;
    const { ok } = await run(
      () =>
        editing
          ? api.put(`/api/admin/event/seasons/${season.id}`, {
              name: form.name,
              endAt: form.endAt,
              texts: config.texts || {},
              npc: config.npc || {},
              modules: config.modules || {},
              items: config.items || {},
              boards: config.boards || [],
            })
          : api.post("/api/admin/event/seasons", {
              kind: form.kind,
              name: form.name,
              startAt: form.startAt,
              endAt: form.endAt,
              viewDays: Number(form.viewDays) || 0,
              config,
            }),
      editing ? "Đã lưu mùa" : "Đã tạo mùa"
    );
    if (ok) {
      onClose();
      onDone();
    }
  };

  return (
    <ActionDialog
      open
      maxWidth="lg"
      title={editing ? `Sửa mùa #${season.id} · ${season.name}` : "Tạo mùa sự kiện"}
      color="info"
      submitText={editing ? "Lưu mùa" : "Tạo mùa"}
      busy={busy}
      disabled={blockers.length > 0}
      onClose={onClose}
      onSubmit={submit}
    >
      <Tabs value={tab} onChange={(e, v) => pickTab(v)} variant="scrollable" scrollButtons="auto">
        {TABS.map((label) => (
          <Tab key={label} label={label} />
        ))}
      </Tabs>
      {started && tab !== 0 && (
        <Alert severity="info">
          Mùa đã bắt đầu: vẫn sửa được chữ, tên và ảnh NPC, tên và ảnh vật phẩm bản riêng. Công
          thức, vật phẩm, map NPC bị khóa để luật chơi không đổi giữa chừng.
        </Alert>
      )}

      {tab === 0 && (
        <MDBox display="flex" flexDirection="column" gap={2}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <MDInput
                select
                fullWidth
                label="Loại event"
                value={form.kind}
                disabled={editing}
                onChange={(e) => pickKind(e.target.value)}
              >
                {kinds.map((kind) => (
                  <MenuItem key={kind.key} value={kind.key}>
                    {kind.name}
                  </MenuItem>
                ))}
              </MDInput>
            </Grid>
            <Grid item xs={12} md={8}>
              <MDInput
                fullWidth
                label="Tên mùa (hiện trên banner, thư thưởng)"
                value={form.name}
                onChange={(e) => set({ name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <MDInput
                fullWidth
                type="datetime-local"
                label="Bắt đầu"
                InputLabelProps={{ shrink: true }}
                value={form.startAt}
                disabled={editing}
                onChange={(e) => set({ startAt: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <MDInput
                fullWidth
                type="datetime-local"
                label="Kết thúc"
                InputLabelProps={{ shrink: true }}
                value={form.endAt}
                helperText={started ? "Mùa đã bắt đầu chỉ được rút ngắn" : null}
                onChange={(e) => set({ endAt: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <MDInput
                fullWidth
                type="number"
                label="Ngày xem sau khi hết"
                value={form.viewDays}
                disabled={editing}
                inputProps={{ min: 0 }}
                onChange={(e) => set({ viewDays: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <MDInput
                fullWidth
                type="number"
                label="+% vàng khi đổi"
                value={bonus}
                disabled={started}
                inputProps={{ min: 0, max: 100 }}
                onChange={(e) => setBonus(e.target.value)}
              />
            </Grid>
          </Grid>
          {!started && (
            <MDBox display="flex" gap={0.5} flexWrap="wrap" alignItems="center">
              <MDTypography variant="caption" color="text" mr={0.5}>
                Chạy trong:
              </MDTypography>
              {DURATIONS.map((d) => (
                <Chip
                  key={d}
                  size="small"
                  variant="outlined"
                  label={`${d} ngày`}
                  onClick={() =>
                    set({
                      startAt: form.startAt || plusDays(null, 0),
                      endAt: plusDays(form.startAt || null, d),
                    })
                  }
                />
              ))}
              <MDTypography variant="caption" color="text" ml={0.5}>
                (tính từ giờ bắt đầu; chưa chọn thì bắt đầu từ bây giờ)
              </MDTypography>
            </MDBox>
          )}
          {startDate && endDate && endDate > startDate && (
            <MDTypography variant="button" color="text">
              Mùa chạy từ <b>{fmt(form.startAt)}</b> tới <b>{fmt(form.endAt)}</b> ({spanDays} ngày),
              chốt thưởng khoảng 2 phút sau giờ kết thúc
              {Number(form.viewDays) > 0
                ? `, người chơi xem kết quả thêm ${form.viewDays} ngày.`
                : "."}
            </MDTypography>
          )}
          {Number(bonus) > 0 && (
            <Alert severity="info">
              Trong mùa, mỗi lần đổi Pet Coin ra vàng được thêm {bonus}% vàng. Menu đổi vàng hiện rõ
              +{bonus}% và hạn. Hai mùa có khuyến mãi không được chạy chồng giờ.
            </Alert>
          )}
          {!editing && (
            <Alert severity="success">
              Các tab tiếp theo để đổi chủ đề: ảnh và tên NPC, chữ, tên và ảnh vật phẩm, công thức
              chế. Mốc quà sửa sau khi tạo, bằng nút Mốc quà ở danh sách mùa.
            </Alert>
          )}
        </MDBox>
      )}
      {/* Hộp thoại con (tạo bản riêng, tìm icon) cũng là form; submit của React đi xuyên portal lên form này. */}
      <MDBox onSubmit={(e) => e.stopPropagation()}>
        {tab === 1 && <AppearanceEditor config={config} started={started} update={update} />}
        {tab === 2 && <ItemsEditor config={config} locked={started} update={update} />}
        {tab === 3 && <CraftEditor config={config} locked={started} update={update} />}
      </MDBox>
      {tab === 4 && (
        <MDInput
          fullWidth
          multiline
          minRows={14}
          maxRows={30}
          label="Cấu hình (JSON)"
          value={jsonText}
          error={Boolean(jsonError)}
          helperText={
            jsonError || "Sửa ở đây thì các tab khác cập nhật theo. JSON sai thì không lưu được."
          }
          inputProps={{ style: { fontFamily: "monospace", fontSize: 12 } }}
          FormHelperTextProps={{ sx: { color: jsonError ? "error.main" : undefined } }}
          onChange={(e) => {
            setJsonText(e.target.value);
            const parsed = parseJson(e.target.value);
            if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
              setJsonError("JSON chưa hợp lệ");
            } else {
              setJsonError(null);
              setConfig(parsed);
            }
          }}
        />
      )}
      {blockers.map((b) => (
        <MDTypography key={b} variant="caption" color="text" display="block">
          • {b}
        </MDTypography>
      ))}
    </ActionDialog>
  );
}

SeasonDialog.defaultProps = { season: null };

SeasonDialog.propTypes = {
  season: PropTypes.shape({
    id: PropTypes.number,
    kind: PropTypes.string,
    name: PropTypes.string,
    startAt: PropTypes.string,
    endAt: PropTypes.string,
    viewDays: PropTypes.number,
    status: PropTypes.number,
    config: PropTypes.string,
  }),
  kinds: PropTypes.arrayOf(
    PropTypes.shape({ key: PropTypes.string, name: PropTypes.string, config: PropTypes.string })
  ).isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};

export default SeasonDialog;
